/**
 * Client-side messaging/CRM SDKs for the app surface:
 *
 *  - Customer.io Data Pipelines browser SDK (@customerio/cdp-analytics-browser;
 *    workspace "Founding Dev App", 221791, JavaScript source 180832) — puts
 *    page views on person timelines and, because in-app is enabled on the
 *    workspace, loads the in-app plugin on its own. The legacy track.js
 *    snippet never connected in-app (the workspace showed "Connect your app"
 *    as required and test sends 400'd), hence the switch. Server-side Track
 *    API (services/customerioService.js on the API) remains the canonical
 *    event emitter; this adds the browser-only capabilities.
 *  - Intercom Messenger — boots identified at login so a "Contact support"
 *    control can `Intercom('show')` from anywhere. The floating launcher is
 *    deliberately hidden except on LAUNCHER_ROUTES (GoSign pattern:
 *    founding-sign/app/javascript/application.js) so it never obstructs the
 *    builder UI.
 *
 * Doctrine (same as everything analytics in this repo): env-gated, guarded
 * off localhost, every call wrapped — messaging must never break the app.
 */

import { stripOneTimeParams } from './urlScrub';

// Browser write key of the JavaScript source — public by design (it ships in
// the bundle), but still env-supplied so staging/prod can differ.
const CIO_WRITE_KEY = process.env.REACT_APP_CUSTOMERIO_CDP_WRITE_KEY;
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

// The SDK is dynamic-imported (keeps it out of the main chunk and off
// localhost), so calls made before it resolves — identify on session restore,
// the first page view — queue here and flush once it loads.
type CioClient = {
  identify: (id: string, traits?: Record<string, unknown>) => unknown;
  page: (category?: string, name?: string, properties?: Record<string, unknown>) => unknown;
  reset: () => unknown;
};
let cio: CioClient | null = null;
let cioPending: Array<(c: CioClient) => void> | null = null;
// Last path sent as a page view (module scope so StrictMode remounts and the
// bridge's mount run can't double-count the first view).
let cioSentPath: string | null = null;

function withCio(fn: (c: CioClient) => void): void {
  if (cio) {
    try { fn(cio); } catch { /* never break the caller */ }
  } else if (cioPending) {
    cioPending.push(fn);
  }
}

// Routes where the floating Intercom launcher is allowed to show. Everywhere
// else the messenger still works — via openSupportMessenger() or any element
// carrying [data-intercom-show] — but the bubble stays hidden.
const LAUNCHER_ROUTES = ['/apps', '/dashboard'];
const LAUNCHER_PREFIXES = ['/settings'];

export interface MessagingIdentity {
  id: string;
  email: string;
  name?: string;
  createdAt?: string | number;
  /** HMAC from the server (/auth/me) — Intercom identity verification. */
  intercomUserHash?: string;
}

declare global {
  interface Window {
    Intercom?: any;
    intercomSettings?: Record<string, unknown>;
  }
}

function isLocalHost(): boolean {
  const h = window.location.hostname;
  return h === 'localhost' || h === '127.0.0.1';
}

function launcherAllowed(pathname: string): boolean {
  return (
    LAUNCHER_ROUTES.includes(pathname) ||
    LAUNCHER_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
  );
}

/**
 * Inject the SDK loaders (stub queues now, real scripts async). Call once at
 * app boot. Neither SDK is identified/booted here — that happens at login via
 * identifyMessaging(). Safe no-op when unconfigured or on localhost.
 */
export function initClientMessaging(): void {
  try {
    if (isLocalHost()) return;

    // Customer.io Data Pipelines SDK. Assign the client inside the callback
    // rather than returning it: the client is thenable, and returning it from
    // a .then would unwrap it into the load result.
    if (CIO_WRITE_KEY && !cio && !cioPending) {
      cioPending = [];
      import('@customerio/cdp-analytics-browser')
        .then(({ AnalyticsBrowser }) => {
          const client = AnalyticsBrowser.load({ writeKey: CIO_WRITE_KEY }) as unknown as CioClient;
          cio = client;
          const queued = cioPending || [];
          cioPending = null;
          queued.forEach((fn) => {
            try { fn(client); } catch { /* ignore */ }
          });
        })
        .catch(() => {
          cioPending = null; /* blocked/failed load: messaging stays off */
        });
    }

    // Intercom loader (stub queue pattern). Boot happens at identify.
    if (INTERCOM_APP_ID && typeof window.Intercom !== 'function') {
      const i: any = function (...args: unknown[]) {
        i.c(args);
      };
      i.q = [];
      i.c = function (args: unknown) {
        i.q.push(args);
      };
      window.Intercom = i;
      const s = document.createElement('script');
      s.async = true;
      s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
      document.head.appendChild(s);
    }

    // "Contact support" delegated trigger — any element with
    // [data-intercom-show] opens the messenger, no import needed (GoSign
    // pattern, works for buttons added later in Settings/Dashboard).
    document.addEventListener('click', (e) => {
      const target = e.target as Element | null;
      const trigger = target?.closest?.('[data-intercom-show]');
      if (!trigger) return;
      e.preventDefault();
      openSupportMessenger();
    });
  } catch {
    /* messaging must never break app boot */
  }
}

/** Identify both SDKs after login. Boots Intercom (launcher hidden by
 *  default; route visibility is handled by updateMessagingOnRoute). */
export function identifyMessaging(u: MessagingIdentity): void {
  try {
    if (isLocalHost() || !u?.id) return;

    withCio((c) =>
      c.identify(String(u.id), {
        email: u.email,
        ...(u.name ? { name: u.name } : {}),
      }),
    );

    if (INTERCOM_APP_ID && typeof window.Intercom === 'function') {
      window.Intercom('boot', {
        app_id: INTERCOM_APP_ID,
        user_id: String(u.id),
        email: u.email,
        ...(u.name ? { name: u.name } : {}),
        ...(u.intercomUserHash ? { user_hash: u.intercomUserHash } : {}),
        hide_default_launcher: !launcherAllowed(window.location.pathname),
      });
    }
  } catch {
    /* never break auth */
  }
}

/** Route-change bridge: CIO page event onto the person timeline + Intercom
 *  "new page" ping + launcher visibility per the allowlist. */
export function updateMessagingOnRoute(pathname: string): void {
  try {
    if (isLocalHost()) return;

    // One page view per distinct path. The URL is captured now, not left to
    // the SDK: calls queued before it loads flush together, and the SDK would
    // stamp every one with the URL at flush time — an auth redirect then read
    // as two views of the final page. The in-app plugin also uses these to
    // re-check page rules on SPA navigation.
    if (cioSentPath !== pathname) {
      cioSentPath = pathname;
      const { href, search } = window.location;
      // No one-time connector code/state on the person timeline (lib/urlScrub).
      const props = { path: pathname, url: stripOneTimeParams(href), search: stripOneTimeParams(search), title: document.title };
      withCio((c) => c.page(undefined, undefined, props));
    }

    if (INTERCOM_APP_ID && typeof window.Intercom === 'function') {
      const show = launcherAllowed(pathname);
      window.Intercom('update', { hide_default_launcher: !show });
      if (!show) window.Intercom('hide');
    }
  } catch {
    /* never break navigation */
  }
}

/** Open the Intercom messenger programmatically (Settings "Contact support"
 *  button etc.). Works on every route regardless of launcher visibility. */
export function openSupportMessenger(): void {
  try {
    if (INTERCOM_APP_ID && typeof window.Intercom === 'function') {
      window.Intercom('show');
    }
  } catch {
    /* ignore */
  }
}

/** "Report a bug" (sidebar, task panel): Intercom straight into a new
 *  message, so they can type the issue right away (Aima, 2026-10-15). Same
 *  guards as openSupportMessenger; a no-op where Intercom isn't loaded
 *  (localhost, an ad blocker). */
export function openBugReport(): void {
  try {
    if (INTERCOM_APP_ID && typeof window.Intercom === 'function') {
      window.Intercom('showNewMessage');
    }
  } catch {
    /* ignore */
  }
}

/** Logout: drop both SDK identities so the next user on this device isn't
 *  stitched to this one. */
export function resetMessaging(): void {
  try {
    withCio((c) => c.reset());
    if (INTERCOM_APP_ID && typeof window.Intercom === 'function') {
      window.Intercom('shutdown');
    }
  } catch {
    /* never break logout */
  }
}
